import { z } from "zod";
import { actorIdentitySchema } from "./authority.js";
import { PENDING_RESOLUTIONS, foldVerificationStatus, NO_AUTHORITY, } from "../verdict-law.js";
import { canonicalSha256, createProtocolSignature, verifyProtocolSignature, } from "./canonicalize.js";
import { COMPLETION_CLASS_SUMMARY_KEYS, completionClassSummariesSchema, completionObligationClassificationSchema, sortProtocolIds, } from "./completion.js";
import { UNIVERSAL_VERIFICATION_PROTOCOL, assuranceClassSchema, evaluatorAuthoritySchema, evidenceBindingSchema, protocolIdSchema, protocolTimestampSchema, protocolVersionIdSchema, protocolVersionSchema, sha256DigestSchema, signatureSchema, verificationStatusSchema, sameEvidenceBinding, } from "./identifiers.js";
// Additive (F1-4 vertical slice): a determination MAY carry the independent
// oracle determinations that were actually resolved for its obligation.
// Empty for every determination that exists today (no live obligation names
// an oracle) and for any pre-existing stored determination read back without
// this field — `.default([])` makes both parse identically to before.
export const oracleDeterminationSchema = z
    .object({
    oracleRef: protocolIdSchema,
    /**
     * Whether the independent record CONTRADICTED the claim.
     *
     * `.default(false)` is the CONSERVATIVE default and is chosen on purpose: a
     * record that does not say it contradicted anything did not, so a stored
     * `ok: false` without this field reads as a REFUSAL — byte-for-byte the
     * verdict such a record produced before MISMATCH existed. Defaulting the
     * other way would let an old row mint an accusation. (Measured 2026-09-12:
     * zero of the 284 stored determinations carry any oracle determination at
     * all, so no stored row takes either branch — the default is a rail, not a
     * migration.)
     */
    contradicted: z.boolean().default(false),
    /**
     * Whether the oracle reached the question and COULD NOT TELL.
     *
     * `.default(false)` for the same conservative reason `contradicted` has
     * one, pointing the other way: a record that does not say it was
     * indeterminate was not, so a stored `ok: false` without this field reads
     * as a REFUSAL — byte-for-byte the verdict such a record produced before
     * this value existed. Defaulting the other way would silently downgrade
     * every historical FAIL into "no finding", which is the mirror of letting
     * an old row mint an accusation and is just as much a re-reading of a
     * signed receipt.
     *
     * Additive beside `contradicted` rather than replacing both with one
     * three-valued field, precisely so that stored rows keep parsing. The two
     * are never written independently: `oracle-resolution.ts` projects them
     * from ONE `ObservationNegativeFinding` the adapter declared, so
     * `{contradicted: true, indeterminate: true}` is not a state this system
     * produces.
     */
    indeterminate: z.boolean().default(false),
    /**
     * Whether the oracle reached the question and it is NOT YET DUE.
     *
     * `.default(false)` for the third time and the same conservative reason: a
     * stored record that does not say it was pending was not, so it keeps
     * reading exactly as it did — a refusal, or a non-finding, per the two
     * flags above. Defaulting the other way would re-read historical verdicts
     * as "not yet", which would be a promise that a deadline exists for records
     * that carry none.
     *
     * Measured 2026-09-12, the same measurement `contradicted` records: the
     * production store holds 81 signed receipts (PASS 78 / FAIL 2 / NOT_RUN 1)
     * and not one carries an oracle determination at all, so no stored row
     * takes any branch of this field. It is a rail, not a migration.
     */
    pending: z.boolean().default(false),
    /**
     * The deadline, and what the window closing means. Non-null exactly when
     * `pending` — `addOracleDeterminationIssues` refuses every other
     * combination, so a pending record with no way out cannot be parsed, let
     * alone signed.
     */
    pendingUntil: protocolTimestampSchema.nullable().default(null),
    pendingResolvesTo: z.enum(PENDING_RESOLUTIONS).nullable().default(null),
    ok: z.boolean(),
    reasonCode: protocolIdSchema,
    statement: z.string().trim().min(1),
})
    .strict()
    .superRefine((record, ctx) => {
    // The correlation, enforced at the PARSE boundary rather than trusted from
    // the one writer. A determination is signed into a receipt; a pending one
    // whose deadline went missing between projection and signature would be a
    // verdict nothing can ever resolve, and the signature would make it
    // permanent. Both directions are refused: a deadline without a pending flag
    // is just as wrong, because it would let a reader believe a resolved
    // determination is still waiting on a clock.
    const hasWindow = record.pendingUntil !== null && record.pendingResolvesTo !== null;
    if (record.pending !== hasWindow) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: record.pending
                ? "oracle determination is pending but carries no { pendingUntil, pendingResolvesTo } deadline"
                : "oracle determination carries a pending deadline without pending: true",
        });
    }
    if (record.pending && record.ok) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "a confirmed oracle determination cannot also be pending",
        });
    }
});
const determinationCommonShape = {
    id: protocolIdSchema,
    obligationId: protocolIdSchema,
    status: verificationStatusSchema,
    authority: evaluatorAuthoritySchema,
    evaluatorRef: protocolIdSchema,
    evaluatorVersion: protocolVersionIdSchema,
    evaluatorDigest: sha256DigestSchema,
    evaluatorActor: actorIdentitySchema,
    evidenceArtifactIds: z.array(protocolIdSchema),
    evidenceDigests: z.array(sha256DigestSchema),
    reasonCodes: z.array(protocolIdSchema),
    rationale: z.string().trim().min(1),
    evaluatedAt: protocolTimestampSchema,
    deterministicIntegrityPassed: z.boolean(),
    oracleDeterminations: z.array(oracleDeterminationSchema).default([]),
};
function addDeterminationIssues(value, context) {
    if (value.status === "PASS" && !value.deterministicIntegrityPassed) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["deterministicIntegrityPassed"],
            message: "PASS requires deterministic evidence-integrity proof",
        });
    }
    if (value.authority === "semantic" &&
        value.status === "PASS" &&
        value.evidenceArtifactIds.length === 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["evidenceArtifactIds"],
            message: "semantic PASS requires admitted evidence",
        });
    }
}
export const determinationSchema = z
    .object({
    ...determinationCommonShape,
    classification: completionObligationClassificationSchema,
})
    .strict()
    .superRefine(addDeterminationIssues);
export const legacyDeterminationSchema = z
    .object(determinationCommonShape)
    .strict()
    .superRefine(addDeterminationIssues);
export const receiptReplaySchema = z
    .object({
    status: z.enum(["REPLAYED", "REPLAYABLE", "BLOCKED", "NOT_AVAILABLE"]),
    environmentDigest: sha256DigestSchema,
    commands: z.array(z.string().trim().min(1)),
    replayedAt: protocolTimestampSchema.nullable(),
    reasonCodes: z.array(protocolIdSchema),
})
    .strict();
const verdictReceiptPayloadCommonShape = {
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    runId: protocolIdSchema,
    binding: evidenceBindingSchema,
    compilationRecordDigest: sha256DigestSchema,
    determinationSetDigest: sha256DigestSchema,
    aggregateStatus: verificationStatusSchema,
    assurance: assuranceClassSchema,
    verifier: actorIdentitySchema,
    policy: z
        .object({
        ref: protocolIdSchema,
        version: protocolVersionIdSchema,
        digest: sha256DigestSchema,
    })
        .strict(),
    independenceDisclosures: z.array(z.string().min(1)),
    replay: receiptReplaySchema,
    issuedAt: protocolTimestampSchema,
    supersedesReceiptId: protocolIdSchema.nullable(),
};
function addUniqueDeterminationIssues(value, context) {
    const obligationIds = value.determinations.map((determination) => determination.obligationId);
    if (new Set(obligationIds).size !== obligationIds.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["determinations"],
            message: "receipt contains more than one determination for an obligation",
        });
    }
}
export const verdictReceiptPayloadSchema = z
    .object({
    ...verdictReceiptPayloadCommonShape,
    determinations: z.array(determinationSchema).min(1),
    completionSummary: completionClassSummariesSchema,
})
    .strict()
    .superRefine((value, context) => {
    addUniqueDeterminationIssues(value, context);
    const expectedSummary = summarizeDeterminations(value.determinations);
    if (JSON.stringify(value.completionSummary) !== JSON.stringify(expectedSummary)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["completionSummary"],
            message: "completion summary must exactly match the signed determination classes",
        });
    }
});
export const legacyVerdictReceiptPayloadSchema = z
    .object({
    ...verdictReceiptPayloadCommonShape,
    determinations: z.array(legacyDeterminationSchema).min(1),
})
    .strict()
    .superRefine(addUniqueDeterminationIssues);
export class CompletionReceiptRefusal extends Error {
    code = "completion-definition-missing";
    constructor() {
        super("completion-definition-missing: receipt has no canonical completion definition");
        this.name = "CompletionReceiptRefusal";
    }
}
export function receiptHasCanonicalCompletionDefinition(payload) {
    return verdictReceiptPayloadSchema.safeParse(payload).success;
}
export const machineReadableVerdictReceiptSchema = z
    .object({
    payload: z.union([verdictReceiptPayloadSchema, legacyVerdictReceiptPayloadSchema]),
    payloadDigest: sha256DigestSchema,
    signature: signatureSchema,
})
    .strict();
export const currentMachineReadableVerdictReceiptSchema = z
    .object({
    payload: verdictReceiptPayloadSchema,
    payloadDigest: sha256DigestSchema,
    signature: signatureSchema,
})
    .strict();
export function isCurrentVerdictReceipt(receipt) {
    return currentMachineReadableVerdictReceiptSchema.safeParse(receipt).success;
}
export function assertCurrentVerdictReceipt(input) {
    const receipt = machineReadableVerdictReceiptSchema.parse(input);
    if (!isCurrentVerdictReceipt(receipt))
        throw new CompletionReceiptRefusal();
    return currentMachineReadableVerdictReceiptSchema.parse(receipt);
}
function receiptAgeIsFresh(input) {
    if (input.now === undefined || input.maxReceiptAgeSeconds === undefined)
        return true;
    const issuedAt = Date.parse(input.issuedAt);
    const now = Date.parse(input.now);
    if (!Number.isFinite(issuedAt) || !Number.isFinite(now))
        return false;
    return now - issuedAt <= input.maxReceiptAgeSeconds * 1000;
}
export function projectCompletionAssurance(input) {
    if (input.supported === false) {
        return {
            kind: "UNSUPPORTED",
            displayLabel: "Unsupported",
            canDisplayVerified: false,
            signedReceiptPresent: Boolean(input.receipt),
            reasonCodes: ["mode-unsupported"],
            status: "UNSUPPORTED",
            assurance: null,
            releaseEvidenceClass: input.releaseEvidenceClass ?? null,
        };
    }
    if (!input.receipt) {
        return {
            kind: input.localCheck?.status === "PASS" ? "CHECK_COMPLETED" : "UNKNOWN",
            displayLabel: input.localCheck?.status === "PASS" ? "Check completed — not verified" : "Unknown",
            canDisplayVerified: false,
            signedReceiptPresent: false,
            reasonCodes: ["signed-receipt-missing"],
            status: input.localCheck?.status ?? "UNKNOWN",
            assurance: null,
            releaseEvidenceClass: input.releaseEvidenceClass ?? null,
        };
    }
    const current = currentMachineReadableVerdictReceiptSchema.safeParse(input.receipt);
    if (!current.success) {
        return {
            kind: "UNKNOWN",
            displayLabel: "Unknown — current completion receipt missing",
            canDisplayVerified: false,
            signedReceiptPresent: true,
            reasonCodes: ["completion-definition-missing"],
            status: "UNKNOWN",
            assurance: null,
            releaseEvidenceClass: input.releaseEvidenceClass ?? null,
        };
    }
    const { payload } = current.data;
    const reasonCodes = [];
    if (input.expectedBinding && !sameEvidenceBinding(payload.binding, input.expectedBinding)) {
        reasonCodes.push("receipt-scope-mismatch");
    }
    if (!receiptAgeIsFresh({
        issuedAt: payload.issuedAt,
        now: input.now,
        maxReceiptAgeSeconds: input.maxReceiptAgeSeconds,
    })) {
        reasonCodes.push("receipt-stale");
    }
    if (payload.assurance !== "PROSPECTIVE")
        reasonCodes.push("assurance-retrospective");
    if (input.releaseEvidenceClass !== undefined &&
        input.releaseEvidenceClass !== null &&
        input.releaseEvidenceClass !== "real_external_workflow") {
        reasonCodes.push(`release-evidence-${input.releaseEvidenceClass}`);
    }
    if (payload.replay.status !== "REPLAYED" && payload.replay.status !== "REPLAYABLE") {
        reasonCodes.push(`replay-${payload.replay.status.toLowerCase()}`);
    }
    const canDisplayVerified = payload.aggregateStatus === "PASS" &&
        payload.assurance === "PROSPECTIVE" &&
        reasonCodes.length === 0;
    if (canDisplayVerified) {
        return {
            kind: "VERIFIED",
            displayLabel: "Verified",
            canDisplayVerified: true,
            signedReceiptPresent: true,
            reasonCodes: [],
            status: payload.aggregateStatus,
            assurance: payload.assurance,
            releaseEvidenceClass: input.releaseEvidenceClass ?? null,
        };
    }
    return {
        kind: payload.assurance === "RETROSPECTIVE" ? "RETROSPECTIVE_RECORD" : "UNKNOWN",
        displayLabel: payload.assurance === "RETROSPECTIVE" ? "Retrospective record — not verified" : "Unknown",
        canDisplayVerified: false,
        signedReceiptPresent: true,
        reasonCodes: reasonCodes.length > 0 ? reasonCodes : [`verdict-${payload.aggregateStatus.toLowerCase()}`],
        status: payload.aggregateStatus,
        assurance: payload.assurance,
        releaseEvidenceClass: input.releaseEvidenceClass ?? null,
    };
}
export function summarizeDeterminations(determinations) {
    const parsed = determinations.map((determination) => determinationSchema.parse(determination));
    const summary = {
        taskOutcome: null,
        baselineHealth: null,
        safety: null,
        effectClosure: null,
    };
    for (const classification of completionObligationClassificationSchema.options) {
        const matches = parsed.filter((determination) => determination.classification === classification);
        if (matches.length === 0)
            continue;
        summary[COMPLETION_CLASS_SUMMARY_KEYS[classification]] = {
            classification: classification,
            status: foldVerificationStatus(matches.map((determination) => ({
                status: determination.status,
                authority: determination.authority,
            }))),
            obligationIds: sortProtocolIds(matches.map((determination) => determination.obligationId)),
        };
    }
    return completionClassSummariesSchema.parse(summary);
}
export function aggregateDeterminations(input) {
    const determinations = input.determinations.map((determination) => determinationSchema.parse(determination));
    const byObligation = new Map(determinations.map((determination) => [determination.obligationId, determination]));
    const members = [];
    for (const obligation of input.obligations) {
        const determination = byObligation.get(obligation.id);
        if (!determination) {
            // A required obligation that produced no determination is a coverage
            // hole. It contributes an unattributed NOT_RUN: there is no evaluator
            // behind it whose authority could be claimed.
            if (obligation.required) {
                members.push({ status: "NOT_RUN", authority: NO_AUTHORITY });
            }
            continue;
        }
        if (obligation.required || input.optionalFailuresAffectAggregate) {
            members.push({ status: determination.status, authority: determination.authority });
        }
    }
    // Coverage is decided above; the verdict is decided by the one law.
    // Determinations carry real evaluator authority, so the PASS authority floor
    // applies here — it is the refusal this tier used to be missing.
    return foldVerificationStatus(members);
}
export function assertCompleteDeterminationSet(input) {
    const ids = new Set(input.determinations.map((row) => row.obligationId));
    for (const obligation of input.obligations) {
        if (obligation.required && !ids.has(obligation.id)) {
            throw new Error(`required obligation ${obligation.id} has no determination`);
        }
    }
}
export function determinationSetDigest(determinations, sha256) {
    const ordered = determinations
        .map((determination) => determinationSchema.parse(determination))
        .sort((left, right) => left.obligationId < right.obligationId ? -1 : left.obligationId > right.obligationId ? 1 : 0);
    return canonicalSha256(ordered, sha256);
}
export function verdictReceiptPayloadDigest(payload, sha256) {
    return canonicalSha256(verdictReceiptPayloadSchema.parse(payload), sha256);
}
function readableVerdictReceiptPayloadDigest(payload, sha256) {
    const current = verdictReceiptPayloadSchema.safeParse(payload);
    const canonicalPayload = current.success
        ? current.data
        : legacyVerdictReceiptPayloadSchema.parse(payload);
    return canonicalSha256(canonicalPayload, sha256);
}
export async function issueVerdictReceipt(input) {
    const payload = verdictReceiptPayloadSchema.parse(input.payload);
    const payloadDigest = verdictReceiptPayloadDigest(payload, input.sha256);
    return currentMachineReadableVerdictReceiptSchema.parse({
        payload,
        payloadDigest,
        signature: await createProtocolSignature(payloadDigest, input.signer),
    });
}
export async function verifyVerdictReceipt(input) {
    const receipt = machineReadableVerdictReceiptSchema.parse(input.receipt);
    const actualDigest = readableVerdictReceiptPayloadDigest(receipt.payload, input.sha256);
    if (actualDigest !== receipt.payloadDigest) {
        return { ok: false, reason: "payload-digest-mismatch" };
    }
    if (receipt.signature.keyId !== input.trustRoot.keyId ||
        receipt.signature.algorithm !== input.trustRoot.algorithm) {
        return { ok: false, reason: "untrusted-key" };
    }
    const signatureValid = await verifyProtocolSignature({
        payloadDigest: receipt.payloadDigest,
        signature: receipt.signature,
        trustRoot: input.trustRoot,
    });
    if (!signatureValid)
        return { ok: false, reason: "signature-invalid" };
    const current = currentMachineReadableVerdictReceiptSchema.safeParse(receipt);
    if (!current.success)
        return { ok: false, reason: "completion-definition-missing" };
    return { ok: true, receipt: current.data };
}
/**
 * The surface projection. MISMATCH joins FAIL on the CONCLUSIVE side rather
 * than falling through the trailing `return "INCOMPLETE"`: that fallthrough
 * means "we could not tell", and routing an affirmative violation into it is
 * precisely the conflation this vocabulary exists to end.
 *
 * MISMATCH did NOT widen this union, and the reasoning was explicit: it orders
 * and colours identically to FAIL, so the distinction is carried by the
 * receipt's own `aggregateStatus` which every consumer also holds. PENDING IS
 * widened, and the difference is the reason. MISMATCH is a refinement of a
 * verdict that is already here; PENDING is a verdict that has not arrived, and
 * the tail it would otherwise fall into says "we could not tell" — the precise
 * thing a reader must NOT be told about a check that is merely not yet due,
 * because "could not tell" is a reason to go and look and "not yet due" is a
 * reason to wait. Ordering and colour differ for the same reason.
 */
export function verdictProjection(receipt) {
    const parsed = machineReadableVerdictReceiptSchema.parse(receipt);
    const current = currentMachineReadableVerdictReceiptSchema.safeParse(parsed);
    if (!current.success)
        return "INCOMPLETE";
    const aggregate = current.data.payload.aggregateStatus;
    if (aggregate === "PASS")
        return "PASS";
    if (aggregate === "FAIL" || aggregate === "MISMATCH") {
        const statuses = new Set(current.data.payload.determinations.map((determination) => determination.status));
        return statuses.has("PASS") ? "PARTIAL" : "FAIL";
    }
    // Named BEFORE the tail, which is the "we could not tell" bucket.
    if (aggregate === "PENDING")
        return "PENDING";
    return "INCOMPLETE";
}
export function createVerdictPayload(input) {
    return verdictReceiptPayloadSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        ...input,
    });
}
